import { SelectableItem } from "shared-ts-ui-action";
import React, { ReactNode, useCallback, useState } from "react";
import { Box, Divider, Paper, SxProps, useTheme } from "@mui/material";
import { PaperElevation } from "shared-ts-ui-elevation";
import { Variant } from "./Variant";
import { NavigationTypeButton } from "./NavigationTypeButton";
import {
  NavigationBar,
  NavigationDrawer,
  NavigationItemObject,
  NavigationRail,
  NavigationType,
} from "shared-ts-ui-navigation";
import {
  AppPageLayout,
  AppPageLayouts,
  AppPageLayoutProvider,
  AppPageVariantProvider,
} from "shared-ts-ui-app-page";
import { useNavigationType } from "./useNavigationType";
import { useFilterNavigationItems } from "./useFilterNavigationItems";
import { useAppLayoutModal } from "./useAppLayoutModal";
import { useSxMerge } from "shared-ts-mui";
import { SizeClass, useWindowWidth } from "shared-ts-ui-responsive";

export interface AppNavigationLayoutProps {
  navigationItems:
    | Array<NavigationItemObject>
    | ((navigationType: NavigationType) => Array<NavigationItemObject>);
  variant?: Variant;
  forceBackgroundForAllVariants?: boolean;
  onNavigationItemSelected: (item: SelectableItem) => void;
  main: ReactNode;
  navigationType?: NavigationType;
  paperElevation?: number;
  disableUserControlledNavigationType?: boolean;
  containerStyle?: SxProps;
}

export const AppNavigationLayout = (props: AppNavigationLayoutProps) => {
  const { main } = props;
  const { modal } = useAppLayoutModal();
  const theme = useTheme();
  const windowWidth = useWindowWidth();
  const appPageLayout = resolveAppPageLayout(windowWidth);
  const variant = props.variant ?? "elevation";
  const disableUserControlledNavigationType =
    props.disableUserControlledNavigationType ?? false;

  const {
    navigationType,
    navigationTypeByWidth,
    navigationTypeByUser,
    toggleNavigationType,
  } = useNavigationType(props.navigationType);

  const navigationItems = useFilterNavigationItems(
    typeof props.navigationItems === "function"
      ? props.navigationItems(navigationType)
      : props.navigationItems,
    modal != null
  );

  const [hovering, setHovering] = useState<boolean>(false);
  const flexDirection = navigationType === "bar" ? "column-reverse" : "row";
  const showNavigationTypeButton =
    !disableUserControlledNavigationType && navigationType !== "bar";

  const handleToggleNavigationTypeByUser = useCallback(() => {
    setHovering(false);
    toggleNavigationType();
  }, [toggleNavigationType]);

  const handleNavigationTypeButtonHover = useCallback((hovering: boolean) => {
    setHovering(hovering);
  }, []);

  const sxContainerStyle = useSxMerge(
    {
      background: theme.palette.background.paper,
      height: "100%",
      width: "100vw",
      display: "flex",
      flexDirection: flexDirection,
      position: "relative",
      // Remove paddingTop from here as it's handled in the global styles
      // Ensure content area takes available space
      overflow: "hidden",
      ...(navigationType === "bar" && {
        // Add specific styles for mobile layout
        // Fix for iOS height calculation
        "@supports (-webkit-touch-callout: none)": {
          // Fix for iOS height calculation
          minHeight: "-webkit-fill-available",
        },
      }),
    },
    props.containerStyle
  );

  return (
    <Paper
      sx={sxContainerStyle}
      className={`AppNavigationLayout AppNavigationLayout-${navigationType}`}
    >
      <PaperElevation elevation={props.paperElevation ?? 1}>
        {navigationType === "bar" && (
          <NavigationBar
            items={navigationItems}
            variant={variant}
            className={"navigation"}
            onSelected={props.onNavigationItemSelected}
            sx={{
              paddingBottom: "env(safe-area-inset-bottom, 0)",
              // Ensure visibility above browser UI
              zIndex: 1,
              position: "relative",
              backgroundColor: theme.palette.primary.light,
            }}
          />
        )}
        {navigationType === "rail" && (
          <NavigationRail
            items={navigationItems}
            variant={variant}
            forceBackgroundForAllVariants={props.forceBackgroundForAllVariants}
            className={"navigation"}
            sx={{ maxWidth: 104, backgroundColor: theme.palette.primary.light }}
            onSelected={props.onNavigationItemSelected}
          />
        )}
        {navigationType === "drawer" && (
          <NavigationDrawer
            items={navigationItems}
            variant={variant}
            forceBackgroundForAllVariants={props.forceBackgroundForAllVariants}
            className={"navigation"}
            sx={{
              backgroundColor: theme.palette.primary.light,
              margin: 1,
              padding: 1,
              ...(variant === "flat" && {
                padding: 1,
              }),
            }}
            onSelected={props.onNavigationItemSelected}
          />
        )}
        {navigationType !== "bar" && (
          <Divider
            orientation={"vertical"}
            sx={{
              position: "relative",
              borderColor: hovering
                ? theme.palette.primary.dark
                : variant === "elevation"
                ? "transparent"
                : undefined,
            }}
          ></Divider>
        )}
        {showNavigationTypeButton && (
          <NavigationTypeButton
            navigationTypeByWidth={navigationTypeByWidth}
            navigationTypeByUser={navigationTypeByUser}
            variant={variant}
            onToggle={handleToggleNavigationTypeByUser}
            onHover={handleNavigationTypeButtonHover}
          />
        )}
        <Box
          className={"main"}
          sx={{
            flexGrow: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            "> *": {
              flexGrow: 1,
              minWidth: 0,
              minHeight: 0,
            },
          }}
        >
          <AppPageLayoutProvider value={appPageLayout}>
            <AppPageVariantProvider value={variant}>
              {modal != null ? modal : main}
            </AppPageVariantProvider>
          </AppPageLayoutProvider>
        </Box>
      </PaperElevation>
    </Paper>
  );
};

const resolveAppPageLayout = (width: SizeClass): AppPageLayout => {
  if (width === SizeClass.Compact) {
    return AppPageLayouts.SwipeableBottomPane;
  } else if (width === SizeClass.Medium) {
    return AppPageLayouts.PanelGroupVertical;
  } else {
    return AppPageLayouts.PanelGroupHorizontal;
  }
};
